import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * Novari button. Pill geometry, one primary voice (ember), an ink variant for
 * strong secondary actions, and quiet outline/ghost for everything else.
 * Press feedback is a 1px settle (CSS, no JS animation per button).
 */
const buttonVariants = cva(
  [
    "relative inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold select-none",
    "transition-[background-color,border-color,color,box-shadow,transform] duration-fast ease-out",
    "focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[rgba(var(--accent-primary-rgb),0.3)]",
    "active:translate-y-px",
    "disabled:pointer-events-none disabled:opacity-45",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          "bg-ember text-ember-on shadow-[inset_0_-2px_0_rgba(0,0,0,0.14)] hover:bg-ember-hover",
        primary:
          "bg-ember text-ember-on shadow-[inset_0_-2px_0_rgba(0,0,0,0.14)] hover:bg-ember-hover",
        ink: "bg-ink-surface text-ink-on shadow-[inset_0_-2px_0_rgba(0,0,0,0.2)] hover:opacity-90",
        destructive:
          "bg-danger text-white shadow-[inset_0_-2px_0_rgba(0,0,0,0.14)] hover:brightness-95",
        outline:
          "border border-line bg-surface text-ink shadow-ledge hover:border-line-strong hover:bg-surface-hover",
        secondary: "bg-surface-sunken text-ink hover:bg-surface-pressed",
        soft: "bg-ember-soft text-ember-soft-ink hover:brightness-[0.98]",
        ghost: "text-ink-2 hover:bg-surface-hover hover:text-ink",
        link: "h-auto px-0 text-ember underline-offset-4 hover:underline active:translate-y-0",
        // Legacy aliases kept so older call sites keep a sensible look.
        professional:
          "bg-ember text-ember-on shadow-[inset_0_-2px_0_rgba(0,0,0,0.14)] hover:bg-ember-hover",
        subtle: "bg-surface-sunken text-ink-2 hover:bg-surface-pressed hover:text-ink",
        white: "bg-surface text-ink border border-line shadow-ledge hover:bg-surface-hover",
      },
      size: {
        default: "h-11 px-5 text-sm rounded-full",
        sm: "h-9 px-3.5 text-[13px] rounded-full",
        lg: "h-12 px-7 text-[15px] rounded-full",
        icon: "h-11 w-11 rounded-full",
        "icon-sm": "h-9 w-9 rounded-full",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Shows a spinner, keeps width stable and blocks repeat submits. */
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, disabled, children, ...props }, ref) => {
    if (asChild) {
      return (
        <Slot className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
          {children}
        </Slot>
      );
    }
    return (
      <button
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading && <Loader2 className="animate-spin" aria-hidden="true" />}
        {children}
      </button>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
